import {
  IconBronzeTrophyColor,
  IconSilverTrophyColor,
  IconTrophyColor,
} from '@macarongfactory/web-icons'
import { Label, Text } from '@macarongfactory/web-ui'

import type { ComponentType } from 'react'

/** 트로피(펜넌트) 배지를 쓰는 최대 순위 */
const MEDAL_MAX_RANK = 3
/** 트로피 아이콘 크기(px) — Figma 16 */
const TROPHY_SIZE_PX = 16
/** 펜넌트 하단 V 노치 (Figma Rectangle3) */
const PENNANT_TAIL_CLIP = 'polygon(0 0, 100% 0, 100% 100%, 50% 50%, 0 100%)'

interface IMedalTone {
  /** 펜넌트 배경 */
  bgClass: string
  /** 순위 텍스트 색 (Text color prop = tailwind text 클래스) */
  textColor: string
  /** 트로피 DS 멀티컬러 아이콘 (web-icons 0.3.3 — 컬러 내장) */
  TrophyIcon: ComponentType<{ width: number; height: number }>
}

/**
 * 등수별 펜넌트 톤 — 1/2/3위 금·은·동.
 */
const MEDAL_TONE: Record<number, IMedalTone> = {
  1: {
    bgClass: 'bg-background-caution-weak',
    textColor: 'text-light-amber-900',
    TrophyIcon: IconTrophyColor,
  },
  2: {
    bgClass: 'bg-light-gray-200',
    textColor: 'text-foreground-neutral-normal',
    TrophyIcon: IconSilverTrophyColor,
  },
  3: {
    bgClass: 'bg-[rgba(183,153,43,0.15)]',
    textColor: 'text-[#6a522c]',
    TrophyIcon: IconBronzeTrophyColor,
  },
}

interface IRankBadgeProps {
  /** 순위 (1~) */
  rank: number
  /**
   * 4위+ 라벨의 세로 위치 클래스 — 레이아웃별 모델명 라인에 맞춘다.
   * 미지정 시 데스크탑 카드 기준 pt-x4(Figma 8462:102980).
   * 모바일 유류비(2행)는 pt-x3.5, 모바일 단일행은 h-[52px] 하단 정렬.
   */
  medalPositionClassName?: string
}

/**
 * 랭킹 순위 배지 — Figma 8624:153047 / 8462:102980
 */
const RankBadge = ({ rank, medalPositionClassName }: IRankBadgeProps) => {
  if (rank > MEDAL_MAX_RANK) {
    return (
      <div
        className={`flex shrink-0 flex-col items-center justify-end whitespace-nowrap ${
          medalPositionClassName ?? 'pt-dimension-x4'
        }`}
      >
        <Label text={`${rank}위`} type='weak' variant='neutral' size='small' />
      </div>
    )
  }

  // 1~3위 — 트로피 펜넌트
  const tone = MEDAL_TONE[rank] ?? MEDAL_TONE[1]
  const { TrophyIcon } = tone

  return (
    <div className='flex shrink-0 flex-col items-start'>
      <div
        className={`flex flex-col items-center px-dimension-x1-5 pt-dimension-x2 ${tone.bgClass}`}
      >
        <TrophyIcon width={TROPHY_SIZE_PX} height={TROPHY_SIZE_PX} />
        <Text variant='caption-1-semibold' color={tone.textColor}>
          {`${rank}위`}
        </Text>
      </div>
      <div
        className={`h-[12px] w-full ${tone.bgClass}`}
        style={{ clipPath: PENNANT_TAIL_CLIP }}
      />
    </div>
  )
}

export default RankBadge
